import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Checkbox,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import stations from '../data/stations';
import { createCampaign } from '../store/slices/campaignsSlice';
import { apiFetch } from '../api/client';
const whatsappTemplateFallback = [
  {
    id: '1929336598018649',
    name: 'adsenginetestimg',
    components: [
      {
        text: 'Hai, pada kesempatan kali ini, contoh pesan yang digunakan oleh Ads Engine menggunakan image sebagai header nya.',
        type: 'BODY'
      },
      {
        format: 'IMAGE',
        type: 'HEADER'
      }
    ]
  },
  {
    id: '4399670890262308',
    name: 'adsenginetest2',
    components: [
      {
        text: 'Salam Hangat,\n\nIni adalah contoh pesan dari Ads Engine.\n\nSehat Selalu,\nAds Engine.',
        type: 'BODY'
      },
      {
        format: 'TEXT',
        text: 'Ads Engine Header',
        type: 'HEADER'
      }
    ]
  },
  {
    id: '928319239872512',
    name: 'adsenginetest',
    components: [
      {
        text: 'Hello Kind Human, \n\nThis is a sample of Ads Engine advertisement message for testing purpose only.\n\nBest Regards,\nAds Engine.',
        type: 'BODY'
      },
      {
        format: 'TEXT',
        text: 'Ads Engine Header',
        type: 'HEADER'
      }
    ]
  }
];

const channels = [{ label: 'Whatsapp', value: 'whatsapp' }];
const sourceChannelOptionsFallback = ['TVM', 'MyMRT-J', 'Chatbot'];
const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const sampleDownloadUrl = API_BASE_URL
  ? `${API_BASE_URL}/api/uploads/phones/sample`
  : '/api/uploads/phones/sample';
const WA_IMAGE_BASE_URL = process.env.REACT_APP_WA_IMAGE_BASE_URL || '/assets/waimages/';
const toLocalIsoDate = (date) => {
  const d = date || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const sortStations = (list) =>
  [...list].sort((a, b) => {
    if (a.display_order !== b.display_order) return a.display_order - b.display_order;
    return a.name.localeCompare(b.name);
  });

const timeToMinutes = (value) => {
  if (!value || typeof value !== 'string') return 0;
  const [hours, minutes] = value.split(':').map((v) => parseInt(v, 10) || 0);
  return hours * 60 + minutes;
};

const mapSourceChannelIds = (items = []) =>
  items.map((item) => (item && typeof item === 'object' && item.id !== undefined ? item.id : item));

const mapStationCodes = (items = []) => items.map((item) => (item && item.code ? item.code : item));

const normalizeTimeValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return value.slice(0, 5);
  }
  return '';
};

const normalizeDay = (day) => {
  if (!day) return '';
  const lower = day.toString().toLowerCase();
  const map = {
    sunday: 'Sun',
    sun: 'Sun',
    monday: 'Mon',
    mon: 'Mon',
    tuesday: 'Tue',
    tue: 'Tue',
    wednesday: 'Wed',
    wed: 'Wed',
    thursday: 'Thu',
    thu: 'Thu',
    friday: 'Fri',
    fri: 'Fri',
    saturday: 'Sat',
    sat: 'Sat'
  };
  return map[lower] || day;
};

const normalizeTimePeriods = (periods = []) =>
  (periods || []).map((p) => ({
    startTime: normalizeTimeValue(p.startTime || p.start_time),
    endTime: normalizeTimeValue(p.endTime || p.end_time),
    days: (p.days || []).map((d) => normalizeDay(d))
  }));

const CampaignCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [initializedFromState, setInitializedFromState] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [uploadError, setUploadError] = useState('');
  const [sampleDownloadError, setSampleDownloadError] = useState('');
  const todayLocal = useMemo(() => toLocalIsoDate(new Date()), []);
  const [detailFetched, setDetailFetched] = useState(false);
  const [sourceChannelOptions, setSourceChannelOptions] = useState(sourceChannelOptionsFallback);
  const [stationOptions, setStationOptions] = useState(stations);
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplateBody, setWaTemplateBody] = useState({ format: '', text: '' });
  const [waHeaderNeedsImage, setWaHeaderNeedsImage] = useState(false);
  const [waHeaderImage, setWaHeaderImage] = useState(null);
  const [waHeaderImageId, setWaHeaderImageId] = useState('');
  const [waHeaderImagePath, setWaHeaderImagePath] = useState('');
  const [waHeaderUploadError, setWaHeaderUploadError] = useState('');
  const [waTemplateText, setWaTemplateText] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const fileInputRef = useRef(null);
  const headerImageInputRef = useRef(null);
  const [campaign, setCampaign] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    priority: '',
    phoneFileName: '',
    fileUploadId: '',
    uploadTotalRows: undefined,
    sourceChannels: [],
    origins: [],
    destinations: [],
    channel: 'whatsapp',
    whatsappTemplateId: '',
    whatsappText: '',
    whatsappHeaderImageId: '',
    whatsappImagePath: '',
    totalImpression: '',
    dailyImpression: '',
    totalPerCustomer: '',
    dailyPerCustomer: '',
    setDaysAndTimes: false,
    timePeriods: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!initializedFromState && location.state?.campaign) {
      const normalizeDateOnly = (value) => {
        if (!value) return '';
        if (typeof value === 'string') {
          if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
          const parsed = new Date(value);
          if (!Number.isNaN(parsed.getTime())) return toLocalIsoDate(parsed);
          return value.slice(0, 10);
        }
        const d = new Date(value);
        if (!Number.isNaN(d.getTime())) return toLocalIsoDate(d);
        return '';
      };
      const normalizedPeriods = normalizeTimePeriods(
        location.state.campaign.timePeriods || location.state.campaign.time_periods || []
      );
      setCampaign((prev) => ({
        ...prev,
            ...location.state.campaign,
            startDate: normalizeDateOnly(location.state.campaign.startDate || location.state.campaign.start_date),
            endDate: normalizeDateOnly(location.state.campaign.endDate || location.state.campaign.end_date),
          sourceChannels: mapSourceChannelIds(
            location.state.campaign.sourceChannels ||
              location.state.campaign.source_channels ||
              prev.sourceChannels
          ),
          origins: mapStationCodes(location.state.campaign.origins || prev.origins),
          destinations: mapStationCodes(location.state.campaign.destinations || prev.destinations),
          timePeriods: normalizedPeriods,
          setDaysAndTimes: normalizedPeriods.length > 0,
      fileUploadId: location.state.campaign.fileUpload?.id || prev.fileUploadId,
      phoneFileName: location.state.campaign.fileUpload?.fileName || prev.phoneFileName,
      uploadTotalRows: location.state.campaign.fileUpload?.totalRow ?? prev.uploadTotalRows,
      channel: location.state.campaign.channel || prev.channel || 'whatsapp',
          whatsappTemplateId:
            location.state.campaign.whatsappTemplateId ||
            location.state.campaign.whatsapp_template_id ||
            prev.whatsappTemplateId ||
            '',
          whatsappText: location.state.campaign.whatsappText || location.state.campaign.whatsapp_text || prev.whatsappText || '',
          whatsappHeaderImageId:
            location.state.campaign.whatsappHeaderImageId ||
            location.state.campaign.whatsapp_header_image_id ||
            prev.whatsappHeaderImageId ||
            '',
          whatsappImagePath:
            location.state.campaign.whatsappImagePath ||
            location.state.campaign.whatsapp_image_path ||
            prev.whatsappImagePath ||
            ''
          }));
          setWaHeaderImageId(
            location.state.campaign.whatsappHeaderImageId ||
              location.state.campaign.whatsapp_header_image_id ||
              ''
          );
          setWaHeaderImagePath(
            location.state.campaign.whatsappImagePath || location.state.campaign.whatsapp_image_path || ''
          );
          setInitializedFromState(true);
        }
      }, [initializedFromState, location.state]);

  useEffect(() => {
    const fetchSourceChannels = async () => {
      try {
        const data = await apiFetch('/api/source-channels');
        const channels = Array.isArray(data.data) ? data.data : [];
        const activeChannels = channels.filter(
          (channel) => (channel.status || '').toUpperCase() === 'ACTIVE'
        );
        setSourceChannelOptions(activeChannels);
      } catch (error) {
        setSourceChannelOptions(sourceChannelOptionsFallback);
      }
    };
    fetchSourceChannels();
  }, []);

  useEffect(() => {
    if (!sourceChannelOptions.length) return;
    setCampaign((prev) => ({
      ...prev,
      sourceChannels: (prev.sourceChannels || []).filter((value) =>
        sourceChannelOptions.some(
          (option) => option === value || option?.id === value || option?.name === value
        )
      )
    }));
  }, [sourceChannelOptions]);

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const data = await apiFetch('/api/stations');
        const list = Array.isArray(data.data) ? data.data : [];
        setStationOptions(list);
      } catch (error) {
        setStationOptions(stations);
      }
    };
    fetchStations();
  }, []);

  useEffect(() => {
    const fetchWhatsappTemplates = async () => {
      try {
        const data = await apiFetch('/api/whatsapp/templates');
        const list = Array.isArray(data.data) ? data.data : data?.data?.items || [];
        const normalized =
          list.length > 0
            ? list.map((tpl, idx) => ({
                id: tpl.id || tpl.name || String(idx),
                name: tpl.name || tpl.id || `Template ${idx + 1}`,
                format: tpl.format || tpl.body?.format || 'TEXT',
                body: tpl.body || tpl.bodyText || tpl.text || '',
                components: tpl.components || []
              }))
            : whatsappTemplateFallback;
        setWaTemplates(normalized);
      } catch (error) {
        setWaTemplates(whatsappTemplateFallback);
      }
    };
    fetchWhatsappTemplates();
  }, []);

  useEffect(() => {
    const fetchDetail = async (id) => {
      setDetailLoading(true);
      setDetailError('');
      try {
        const data = await apiFetch(`/api/campaigns/${id}`);
        const detail = data.data || {};
        const normalizedPeriods = normalizeTimePeriods(detail.timePeriods || detail.time_periods || []);
        const normalizeDateOnly = (value) => {
          if (!value) return '';
          if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
            const parsed = new Date(value);
            if (!Number.isNaN(parsed.getTime())) return toLocalIsoDate(parsed);
            return value.slice(0, 10);
          }
          const d = new Date(value);
          if (!Number.isNaN(d.getTime())) return toLocalIsoDate(d);
          return '';
        };
        setCampaign((prev) => ({
          ...prev,
          ...detail,
          startDate: normalizeDateOnly(detail.startDate || detail.start_date || prev.startDate),
          endDate: normalizeDateOnly(detail.endDate || detail.end_date || prev.endDate),
          description: detail.description ?? '',
          priority: detail.priority ?? '',
            sourceChannels:
              mapSourceChannelIds(detail.sourceChannels || detail.source_channels || prev.sourceChannels),
          channel: detail.channel || prev.channel,
          whatsappTemplateId: detail.whatsappTemplateId || detail.whatsapp_template_id || prev.whatsappTemplateId || '',
          whatsappText: detail.whatsappText || detail.whatsapp_text || prev.whatsappText || '',
          totalImpression: detail.totalImpression ?? detail.total_impression ?? prev.totalImpression,
          dailyImpression: detail.dailyImpression ?? detail.daily_impression ?? prev.dailyImpression,
          totalPerCustomer:
            detail.totalImpressionPerCustomer ?? detail.total_impression_per_customer ?? prev.totalPerCustomer,
          dailyPerCustomer:
            detail.dailyImpressionPerCustomer ?? detail.daily_impression_per_customer ?? prev.dailyPerCustomer,
          timePeriods: normalizedPeriods.length ? normalizedPeriods : prev.timePeriods,
          fileUploadId: detail.fileUpload?.id || detail.fileUploadId || detail.file_upload_id || prev.fileUploadId,
          phoneFileName: detail.fileUpload?.fileName || detail.fileName || prev.phoneFileName,
          uploadTotalRows: detail.fileUpload?.totalRow ?? prev.uploadTotalRows,
            origins: mapStationCodes(detail.origins || prev.origins),
            destinations: mapStationCodes(detail.destinations || prev.destinations),
          setDaysAndTimes: normalizedPeriods.length > 0 ? true : prev.setDaysAndTimes,
          whatsappHeaderImageId:
            detail.whatsappImageUploadId ||
            detail.whatsappHeaderImageId ||
            detail.whatsapp_header_image_id ||
            prev.whatsappHeaderImageId ||
            '',
          whatsappImagePath:
            detail.whatsappImagePath || detail.whatsapp_image_path || prev.whatsappImagePath || '',
          waHeaderImagePath: detail.whatsappImagePath || detail.whatsapp_image_path || ''
        }));
        setWaHeaderImageId(
          detail.whatsappImageUploadId ||
            detail.whatsappHeaderImageId ||
            detail.whatsapp_header_image_id ||
            ''
        );
        setWaHeaderImagePath(detail.whatsappImagePath || detail.whatsapp_image_path || '');
        setWaTemplateText(detail.whatsappText || detail.whatsapp_text || '');
      } catch (error) {
        setDetailError(error.message || 'Failed to load campaign');
      } finally {
        setDetailFetched(true);
        setDetailLoading(false);
      }
    };
    if (campaign.id && !detailFetched) {
      fetchDetail(campaign.id);
    }
  }, [campaign.id, detailFetched]);

  const sortedStationOptions = useMemo(() => sortStations(stationOptions), [stationOptions]);

  const handleChange = (key) => (event) => {
    setCampaign((prev) => ({
      ...prev,
      [key]: event.target.value
    }));
  };

  const applyTemplateDetails = useCallback(
    (templateId, list = waTemplates) => {
      const tpl = list.find((t) => String(t.id) === String(templateId));
      const bodyField = tpl?.body;
      const bodyComponent = tpl?.components?.find((c) => c.type === 'BODY') || {};
      const text =
      typeof bodyField === 'string'
        ? bodyField
        : bodyField?.text || bodyComponent.text || '';
    const format =
      tpl?.format ||
      (typeof bodyField === 'object' && bodyField?.format) ||
      bodyComponent.format ||
      'TEXT';
    setWaTemplateBody({
      format,
      text
    });
    setWaTemplateText(text || '');
    const header = tpl?.components?.find((c) => c.type === 'HEADER');
    const needsImage = header?.format === 'IMAGE' || format === 'IMAGE';
    setWaHeaderNeedsImage(needsImage);
    if (!needsImage) {
      setWaHeaderImage(null);
      setWaHeaderImageId('');
      if (headerImageInputRef.current) headerImageInputRef.current.value = '';
    }
    },
    [waTemplates]
  );

  useEffect(() => {
    if (campaign.whatsappTemplateId && waTemplates.length) {
      applyTemplateDetails(campaign.whatsappTemplateId, waTemplates);
    }
  }, [campaign.whatsappTemplateId, waTemplates, applyTemplateDetails]);

  const handleTemplateChange = (event) => {
    const value = event.target.value || '';
    setCampaign((prev) => ({
      ...prev,
      whatsappTemplateId: value
    }));
    setErrors((prev) => ({ ...prev, whatsappTemplateId: '' }));
    applyTemplateDetails(value);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploadStatus('loading');
    setCampaign((prev) => ({
      ...prev,
      uploadTotalRows: undefined
    }));
    const formData = new FormData();
    formData.append('file', file);
    try {
      const data = await apiFetch('/api/uploads/phones', {
        method: 'POST',
        body: formData
      });
      setCampaign((prev) => ({
        ...prev,
        phoneFileName: data.fileName || file.name,
        fileUploadId: data.id || data.fileUploadId || '',
        uploadTotalRows: data.totalRow
      }));
      setUploadStatus('succeeded');
    } catch (error) {
      setUploadError(error.message || 'Failed to upload file');
      setCampaign((prev) => ({
        ...prev,
        phoneFileName: '',
        fileUploadId: '',
        uploadTotalRows: undefined
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setUploadStatus('failed');
    }
  };

  const handleHeaderImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setWaHeaderUploadError('');
    setWaHeaderImage(file);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await apiFetch('/api/uploads/wa-images', {
        method: 'POST',
        body: formData
      });
      setWaHeaderImageId(data.id || data.fileUploadId || data.file_upload_id || '');
      setWaHeaderImagePath(data.storedPath || data.stored_path || '');
    } catch (err) {
      setWaHeaderImage(null);
      setWaHeaderImageId('');
      setWaHeaderImagePath('');
      if (headerImageInputRef.current) headerImageInputRef.current.value = '';
      setWaHeaderUploadError(err.message || 'Failed to upload header image');
    }
  };

  const handleCancel = () => {
    navigate('/campaigns');
  };

  const handleDownloadSample = async (event) => {
    event.preventDefault();
    setSampleDownloadError('');
    try {
      const response = await apiFetch(sampleDownloadUrl, { parseAs: 'blob' });
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const contentType = response.headers.get('Content-Type') || '';
      let filename = 'sample-phone-upload';
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/i);
        if (match && match[1]) {
          filename = match[1];
        }
      } else if (contentType.includes('excel')) {
        filename = `${filename}.xlsx`;
      } else if (contentType.includes('csv')) {
        filename = `${filename}.csv`;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSampleDownloadError(error.message || 'Failed to download sample file');
    }
  };

  const handleSave = async (status) => {
    if (status === 'publish') {
      const requiredFields = {
        name: 'Campaign Name is required',
        priority: 'Priority is required',
        startDate: 'Start Date is required',
        endDate: 'End Date is required',
        whatsappTemplateId: 'Whatsapp Template Id is required',
        totalImpression: 'Total Impression is required',
        dailyImpression: 'Daily Impression is required',
        totalPerCustomer: 'Total Impression per Customer is required',
        dailyPerCustomer: 'Daily Impression per Customer is required'
      };

      const nextErrors = Object.entries(requiredFields).reduce((acc, [key, message]) => {
        const value = campaign[key];
        const isEmpty =
          value === undefined ||
          value === null ||
          value === '' ||
          (typeof value === 'string' && value.trim() === '');
        if (isEmpty) {
          acc[key] = message;
        }
        return acc;
      }, {});

      if (campaign.priority !== '' && campaign.priority !== null && campaign.priority !== undefined) {
        const priorityValue = Number(campaign.priority);
        if (!Number.isFinite(priorityValue) || priorityValue <= 0) {
          nextErrors.priority = 'Priority must be a positive number.';
        }
      }

      if (campaign.startDate) {
        const todayStr = toLocalIsoDate(new Date());
        if (campaign.startDate < todayStr) {
          nextErrors.startDate = 'Start Date cannot be before today.';
        }
      }

      if (campaign.startDate && campaign.endDate) {
        if (campaign.endDate < campaign.startDate) {
          nextErrors.endDate = 'End Date cannot be before Start Date.';
        }
      }

      if (waHeaderNeedsImage && !waHeaderImageId) {
        nextErrors.whatsappHeaderImageId = 'Header image is required for this template.';
      }

      if (campaign.setDaysAndTimes) {
        if (!campaign.timePeriods.length) {
          nextErrors.timePeriodsGeneral = 'Add at least one time period.';
        } else {
          const periodErrors = campaign.timePeriods.map(() => '');
          const isMidnightEnd = (value) => {
            if (!value) return false;
            const normalized = typeof value === 'string' ? value.slice(0, 5) : value;
            return normalized === '00:00';
          };
          campaign.timePeriods.forEach((period, idx) => {
            if (!period.days?.length) {
              periodErrors[idx] = 'Select at least one day.';
            } else if (
              timeToMinutes(period.endTime) <= timeToMinutes(period.startTime) &&
              !isMidnightEnd(period.endTime)
            ) {
              periodErrors[idx] = 'End time must be after start time.';
            }
          });
          if (periodErrors.some(Boolean)) {
            nextErrors.timePeriods = periodErrors;
          }
        }
      }

      setErrors(nextErrors);

      if (Object.keys(nextErrors).length > 0) {
        return;
      }

      const previewSourceChannels = (campaign.sourceChannels || []).map((item) => {
        if (item && typeof item === 'object') {
          return {
            id: item.id ?? item.name ?? item.value ?? item,
            name: item.name ?? item.label ?? item.id ?? item.value ?? item
          };
        }
        const match =
          sourceChannelOptions.find(
            (opt) => opt === item || opt?.id === item || opt?.name === item || opt?.label === item
          ) || {};
        if (!match.id && !match.name && !match.label) {
          return null;
        }
        return {
          id: match.id ?? item,
          name: match.name ?? match.label ?? item
        };
      });
      const findStation = (code) =>
        stationOptions.find((st) => st.code === code) ||
        stations.find((st) => st.code === code) || { code };
      const previewOrigins = (campaign.origins || []).map((code) => findStation(code));
      const previewDestinations = (campaign.destinations || []).map((code) => findStation(code));

      navigate('/campaigns/new/preview', {
        state: {
          campaign: {
            ...campaign,
            status: 'PENDING_APPROVAL',
            sourceChannels: previewSourceChannels.filter(Boolean),
            origins: previewOrigins,
            destinations: previewDestinations,
            whatsappText: waTemplateText || campaign.whatsappText,
            whatsappHeaderImageId: waHeaderImageId,
            whatsappFormat: waTemplateBody.format || campaign.whatsappFormat || campaign.whatsapp_format || '',
            whatsappImagePath: waHeaderImagePath || campaign.whatsappImagePath || ''
          }
        }
      });
      return;
    }

    if (!campaign.name) {
      setErrors((prev) => ({ ...prev, name: 'Campaign Name is required' }));
      return;
    }

    const draftPayload = {
      ...(campaign.id ? { id: campaign.id } : {}),
      name: campaign.name || '',
      status: 'DRAFT',
      description: campaign.description || '',
      priority: campaign.priority || '-',
      startDate: campaign.startDate || '',
      endDate: campaign.endDate || '',
      origins: campaign.origins,
      destinations: campaign.destinations,
      sourceChannels: mapSourceChannelIds(campaign.sourceChannels),
      channel: campaign.channel,
      whatsappTemplateId: campaign.whatsappTemplateId || '',
      whatsappText: waTemplateText || campaign.whatsappText || '',
      whatsappHeaderImageId: waHeaderImageId,
      totalImpression: campaign.totalImpression,
      dailyImpression: campaign.dailyImpression,
      totalImpressionPerCustomer: campaign.totalPerCustomer,
      dailyImpressionPerCustomer: campaign.dailyPerCustomer,
      timePeriods: campaign.timePeriods,
      fileUploadId: campaign.fileUploadId
    };

    try {
      await dispatch(createCampaign(draftPayload)).unwrap();
      navigate('/campaigns');
    } catch (error) {
      setErrors((prev) => ({ ...prev, api: error || 'Failed to save draft' }));
    }
  };

  const renderMultiSelectValue = (selected) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value) => {
        const station =
          sortedStationOptions.find((item) => item.code === value) ||
          stations.find((item) => item.code === value);
        const label = station?.name || station?.alias || value;
        return <Chip key={value} label={label} size="small" />;
      })}
    </Box>
  );

  const renderSimpleMultiValue = (selected) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value) => {
        const match =
          sourceChannelOptions.find(
            (option) => option === value || option?.id === value || option?.name === value
          ) || {};
        const label = match.name || match.id || value;
        if (!match.name && !match.id) return null;
        return <Chip key={value} label={label} size="small" />;
      })}
    </Box>
  );

  const timeOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < 24 * 4; i += 1) {
      const hours = Math.floor(i / 4);
      const minutes = (i % 4) * 15;
      const labelHours = ((hours + 11) % 12) + 1;
      const suffix = hours >= 12 ? 'PM' : 'AM';
      const labelMinutes = minutes.toString().padStart(2, '0');
      const value = `${hours.toString().padStart(2, '0')}:${labelMinutes}`;
      const label = `${labelHours}:${labelMinutes} ${suffix}`;
      options.push({ label, value });
    }
    return options;
  }, []);

  const toggleDay = (periodIndex, day) => {
    setCampaign((prev) => {
      const next = { ...prev };
      const period = next.timePeriods[periodIndex];
      const hasDay = period.days.includes(day);
      const nextDays = hasDay ? period.days.filter((d) => d !== day) : [...period.days, day];
      next.timePeriods = prev.timePeriods.map((p, idx) =>
        idx === periodIndex ? { ...p, days: nextDays } : p
      );
      return next;
    });
  };

  const updatePeriodField = (periodIndex, key, value) => {
    setCampaign((prev) => ({
      ...prev,
      timePeriods: prev.timePeriods.map((p, idx) =>
        idx === periodIndex ? { ...p, [key]: value } : p
      )
    }));
  };

  const addPeriod = () => {
    setCampaign((prev) => ({
      ...prev,
      timePeriods: [
        ...prev.timePeriods,
        {
          startTime: '00:00',
          endTime: '00:00',
          days: [...weekDays]
        }
      ]
    }));
  };

  const removePeriod = (periodIndex) => {
    setCampaign((prev) => ({
      ...prev,
      timePeriods: prev.timePeriods.filter((_, idx) => idx !== periodIndex)
    }));
  };

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">
              {campaign.id ? 'Edit Campaign' : 'New Campaign'}
            </Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {campaign.id ? `Edit Campaign #${campaign.id}` : 'New Campaign'}
          </Typography>
          <Typography color="text.secondary">
            Define campaign details, targeting, channel, and frequency controls.
          </Typography>
        </Box>
      </Box>

      {detailLoading && (
        <Paper sx={{ p: 2, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
          <Typography>Loading campaign data...</Typography>
        </Paper>
      )}
      {detailError && (
        <Paper sx={{ p: 2, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
          <Typography color="error">{detailError}</Typography>
        </Paper>
      )}

      {!detailLoading && (
        <>
      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={3}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Basic Campaign Information
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2
            }}
          >
            <TextField
              label="Campaign Name"
              placeholder="Year End Promo"
              value={campaign.name ?? ''}
              onChange={handleChange('name')}
              fullWidth
              required
              error={Boolean(errors.name)}
              helperText={errors.name}
            />
            <TextField
              label="Priority"
              placeholder="1"
              type="number"
              value={campaign.priority ?? ''}
              onChange={handleChange('priority')}
              inputProps={{ min: 1, step: 1 }}
              fullWidth
              required
              error={Boolean(errors.priority)}
              helperText={errors.priority || 'Lower number = higher priority.'}
            />
            <TextField
              label="Start Date"
              type="date"
              value={campaign.startDate ?? ''}
              onChange={handleChange('startDate')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ style: { cursor: 'pointer' }, min: todayLocal }}
              fullWidth
              required
              error={Boolean(errors.startDate)}
              helperText={errors.startDate}
            />
            <TextField
              label="End Date"
              type="date"
              value={campaign.endDate ?? ''}
              onChange={handleChange('endDate')}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                style: { cursor: 'pointer' },
                min: campaign.startDate || todayLocal
              }}
              fullWidth
              required
              error={Boolean(errors.endDate)}
              helperText={errors.endDate}
            />
            <TextField
              label="Description (optional)"
              placeholder="Add context about objectives or audience"
              value={campaign.description ?? ''}
              onChange={handleChange('description')}
              fullWidth
              multiline
              minRows={3}
              sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}
            />
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={3}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Targeting
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2
            }}
          >
            <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
              <FormControl fullWidth>
                <InputLabel>Source Channel</InputLabel>
              <Select
                multiple
                value={campaign.sourceChannels || []}
                onChange={handleChange('sourceChannels')}
                input={<OutlinedInput label="Source Channel" />}
                renderValue={renderSimpleMultiValue}
              >
                  {sourceChannelOptions.map((option) => {
                    const value = option.id || option.name || option;
                    const label = option.name || option;
                    return (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Phone Number (Upload)
                </Typography>
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    backgroundColor: 'background.paper'
                  }}
                >
                  <Button variant="outlined" component="label" sx={{ minWidth: 160 }}>
                    Upload File
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept=".csv,.xlsx,.xls,.txt"
                      onChange={handleFileUpload}
                    />
                  </Button>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {campaign.phoneFileName || 'No file selected'}
                    </Typography>
                    {campaign.uploadTotalRows !== undefined && campaign.uploadTotalRows !== null && (
                      <Typography variant="body2" color="text.secondary">
                        Total rows: {campaign.uploadTotalRows}
                      </Typography>
                    )}
                  </Box>
                  {uploadStatus === 'failed' && uploadError && (
                    <Typography color="error" variant="body2">
                      {uploadError}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2">
                  <Link href={sampleDownloadUrl} underline="hover" onClick={handleDownloadSample}>
                    Download sample file
                  </Link>
                  {sampleDownloadError && (
                    <Typography color="error" variant="body2">
                      {sampleDownloadError}
                    </Typography>
                  )}
                </Typography>
              </Stack>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Origin Station</InputLabel>
              <Select
                multiple
                value={campaign.origins || []}
                onChange={handleChange('origins')}
                input={<OutlinedInput label="Origin Station" />}
                renderValue={renderMultiSelectValue}
              >
                {sortedStationOptions.map((station) => (
                  <MenuItem key={station.code} value={station.code}>
                    {station.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Destination</InputLabel>
              <Select
                multiple
                value={campaign.destinations || []}
                onChange={handleChange('destinations')}
                input={<OutlinedInput label="Destination" />}
                renderValue={renderMultiSelectValue}
              >
                {sortedStationOptions.map((station) => (
                  <MenuItem key={station.code} value={station.code}>
                    {station.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={3}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Channel
          </Typography>
          <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select value={campaign.channel || ''} label="Channel" onChange={handleChange('channel')}>
                  {channels.map((channel) => (
                    <MenuItem key={channel.value} value={channel.value}>
                      {channel.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {campaign.channel === 'whatsapp' && (
              <Stack spacing={2}>
                <FormControl fullWidth required error={Boolean(errors.whatsappTemplateId)}>
                  <InputLabel>WhatsApp Template</InputLabel>
                  <Select
                    value={campaign.whatsappTemplateId}
                    label="WhatsApp Template"
                    onChange={handleTemplateChange}
                  >
                    {waTemplates.map((tpl, idx) => (
                      <MenuItem key={tpl.id || tpl.name || idx} value={tpl.id || tpl.name || ''}>
                        {tpl.name || tpl.id || `Template ${idx + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.whatsappTemplateId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.whatsappTemplateId}
                    </Typography>
                  )}
                </FormControl>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: 'background.default'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Body Preview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Format: {waTemplateBody.format || '-'}
                  </Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {waTemplateBody.text || 'No body content'}
                  </Typography>
                </Paper>
                {waHeaderNeedsImage && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Header Image
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      component="label"
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Upload Image
                      <input
                        ref={headerImageInputRef}
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={handleHeaderImageUpload}
                      />
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {waHeaderImage ? waHeaderImage.name : 'No image selected'}
                    </Typography>
                    {(waHeaderImageId || waHeaderImagePath || campaign.whatsappImagePath) && (
                      <Box
                        component="img"
                        src={
                          waHeaderImagePath
                            ? `${WA_IMAGE_BASE_URL}${waHeaderImagePath.split(/[/\\]/).pop()}`
                            : campaign.whatsappImagePath
                              ? `${WA_IMAGE_BASE_URL}${campaign.whatsappImagePath.split(/[/\\]/).pop()}`
                              : ''
                        }
                        alt="WhatsApp Header"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 220,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          objectFit: 'contain',
                          backgroundColor: 'background.paper'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                    {(waHeaderUploadError || errors.whatsappHeaderImageId) && (
                      <Typography variant="body2" color="error">
                        {waHeaderUploadError || errors.whatsappHeaderImageId}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={3}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Frequency Control
          </Typography>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                Campaign Level
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2
                }}
              >
                <TextField
                  label="Total Impression (0: Unlimitted)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={campaign.totalImpression ?? ''}
                  onChange={handleChange('totalImpression')}
                  fullWidth
                  required
                  placeholder="Total Impression (0: Unlimitted)"
                  error={Boolean(errors.totalImpression)}
                  helperText={errors.totalImpression}
                />
                <TextField
                  label="Daily Impression (0: Unlimitted)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={campaign.dailyImpression ?? ''}
                  onChange={handleChange('dailyImpression')}
                  fullWidth
                  required
                  placeholder="Daily Impression (0: Unlimitted)"
                  error={Boolean(errors.dailyImpression)}
                  helperText={errors.dailyImpression}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                Customer Level
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2
                }}
              >
                <TextField
                  label="Total Impression per Customer (0: Unlimitted)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={campaign.totalPerCustomer ?? ''}
                  onChange={handleChange('totalPerCustomer')}
                  fullWidth
                  required
                  placeholder="Total Impression per Customer (0: Unlimitted)"
                  error={Boolean(errors.totalPerCustomer)}
                  helperText={errors.totalPerCustomer}
                />
                <TextField
                  label="Daily Impression per Customer (0: Unlimitted)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={campaign.dailyPerCustomer ?? ''}
                  onChange={handleChange('dailyPerCustomer')}
                  fullWidth
                  required
                  placeholder="Daily Impression per Customer (0: Unlimitted)"
                  error={Boolean(errors.dailyPerCustomer)}
                  helperText={errors.dailyPerCustomer}
                />
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={3}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Day and time
          </Typography>
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={campaign.setDaysAndTimes}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setCampaign((prev) => ({
                      ...prev,
                      setDaysAndTimes: checked,
                      timePeriods:
                        checked && prev.timePeriods.length
                          ? prev.timePeriods
                          : checked
                            ? [
                                {
                                  startTime: '00:00',
                                  endTime: '00:00',
                                  days: [...weekDays]
                                }
                              ]
                            : []
                    }));
                  }}
                />
              }
              label="Set days and times"
            />
            {campaign.setDaysAndTimes && (
              <Stack spacing={2}>
                {errors.timePeriodsGeneral && (
                  <Typography color="error" variant="body2">
                    {errors.timePeriodsGeneral}
                  </Typography>
                )}
                {campaign.timePeriods.map((period, idx) => {
                  const periodError =
                    Array.isArray(errors.timePeriods) && errors.timePeriods[idx]
                      ? errors.timePeriods[idx]
                      : '';
                  return (
                    <Box
                      key={idx}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <FormControl fullWidth>
                          <InputLabel>Start time</InputLabel>
                          <Select
                            label="Start time"
                            value={period.startTime}
                            onChange={(event) => updatePeriodField(idx, 'startTime', event.target.value)}
                          >
                            {timeOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel>End time</InputLabel>
                          <Select
                            label="End time"
                            value={period.endTime}
                            onChange={(event) => updatePeriodField(idx, 'endTime', event.target.value)}
                          >
                            {timeOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton
                          aria-label="remove period"
                          onClick={() => removePeriod(idx)}
                          size="small"
                          sx={{ mt: 0.5 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                          Repeating on *
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {weekDays.map((day) => {
                            const selected = period.days.includes(day);
                            return (
                              <Chip
                                key={day}
                                label={day}
                                color={selected ? 'primary' : 'default'}
                                variant={selected ? 'filled' : 'outlined'}
                                onClick={() => toggleDay(idx, day)}
                                sx={{ borderRadius: 20 }}
                              />
                            );
                          })}
                        </Stack>
                      </Box>
                      {periodError && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                          {periodError}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
                <Box>
                  <Button
                    variant="text"
                    size="small"
                    onClick={addPeriod}
                    sx={{ textTransform: 'none', px: 0 }}
                  >
                    Add time period
                  </Button>
                </Box>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
        <Button variant="outlined" color="inherit" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="outlined" onClick={() => handleSave('draft')}>
          Save Draft
        </Button>
        <Button variant="contained" color="primary" onClick={() => handleSave('publish')}>
          Request Approval
        </Button>
      </Stack>
        </>
      )}
    </Stack>
  );
};

export default CampaignCreatePage;
